'use client'

import { Avatar } from '@repo/ui/Avatar'
import { CircledCheckIcon } from '@repo/ui/icons/CircledCheckIcon'
import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import { DataGridPro, GridColDef, gridClasses } from '@mui/x-data-grid-pro'
import { formatDate } from '@repo/utils/time'
import { Pill } from '@repo/ui/Pill'
import { useEffect, useState } from 'react'
import { useFeeEarnerStore } from '@repo/store/useFeeEarnerStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useTheme } from '@mui/material/styles'
import { useUndertakingsStore } from '@repo/store/useUndertakingsStore'
import { useUrlStore } from '@repo/store/useUrlStore'
import { v4 as uuidv4 } from 'uuid'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import MUITooltip from '@mui/material/Tooltip'

export const UndertakingsTableModule = () => {
  const { feeEarnersById } = useFeeEarnerStore()
  const { currentMatter } = useMattersStore()
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { fetchAllUndertakingsByClientNoAndMatterNo, undertakings } = useUndertakingsStore()
  const { navigateWithClientMatterNo } = useUrlStore()

  const [loading, setLoading] = useState(false)

  const theme = useTheme()

  useEffect(() => {
    if (!currentMatter) return

    const fetchUndertakings = async () => {
      try {
        setLoading(true)
        await fetchAllUndertakingsByClientNoAndMatterNo({
          clientNo: currentMatter.clientNo,
          matterNo: currentMatter.matterNo
        })
        setLoading(false)
      } catch (error) {
        console.error('Error fetching undertakings:', error)
      }
    }

    fetchUndertakings()
  }, [fetchAllUndertakingsByClientNoAndMatterNo, currentMatter])

  const handleNavigate = async ({ clientNo, matterNo }: any) => {
    await navigateWithClientMatterNo({
      route: '/case-management/matters/custom-data-pages',
      appendClientMatterNo: false,
      clientMatterNo: `${clientNo}-${matterNo}`
    })
  }

  const columns: GridColDef[] = [
    {
      field: 'spacer',
      headerName: '',
      width: 10,
      hideable: false
    },
    {
      field: 'clientMatterNo',
      headerName: 'Matter no',
      width: 120,
      type: 'string',
      align: 'left',
      hideable: false,
      renderCell: (params: any) => (
        <ClientMatterDetailsPill matterNo={params.row.matterNo} clientNo={params.row.clientNo} sx={{ top: '3px' }} />
      )
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
      minWidth: 300,
      type: 'string',
      flex: 1,
      hideable: false,
      renderCell: (params: any) => {
        const text = (
          <MUITypography sx={{ mb: 0 }} noWrap>
            {params.row.description || '-'}
          </MUITypography>
        )

        if (params.row.description?.length > 100) {
          return (
            <MUITooltip title={params.row.description} arrow placement="top">
              {text}
            </MUITooltip>
          )
        }

        return text
      }
    },

    {
      field: 'undertakingCategory',
      headerName: 'Category',
      type: 'string',
      width: 240,
      hideable: false,
      renderCell: (params: any) => (
        <MUITypography sx={{ mb: 0 }}>
          {params.row.undertakingCategory && !params.row.undertakingCategory.toLowerCase().includes('please select')
            ? params.row.undertakingCategory
            : '-'}
        </MUITypography>
      )
    },
    {
      field: 'undertakingType',
      headerName: 'Type',
      width: 160,
      type: 'string',
      hideable: false,
      renderCell: (params: any) => (
        <MUITypography sx={{ mb: 0 }}>
          {params.row.undertakingType && !params.row.undertakingType.toLowerCase().includes('please select')
            ? params.row.undertakingType
            : '-'}
        </MUITypography>
      )
    },
    {
      field: 'responsibleFeeEarnerId ',
      headerName: 'Fee earner',
      width: 200,
      type: 'custom',
      renderCell: (params: any) => {
        const { responsibleFeeEarnerId } = params.row

        const feeEarnerId =
          responsibleFeeEarnerId && !responsibleFeeEarnerId.toLowerCase().includes('please select')
            ? responsibleFeeEarnerId
            : null

        const { description: feeEarnerDescription } =
          feeEarnerId && feeEarnersById[feeEarnerId] ? feeEarnersById[feeEarnerId] : {}

        if (!feeEarnerDescription || !feeEarnerId) {
          return <MUITypography sx={{ mb: 0 }}>-</MUITypography>
        }

        return (
          <MUIStack display="flex" direction="row" sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
            <Avatar
              feeEarnerId={feeEarnerId}
              name={feeEarnerDescription}
              sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }}
            />
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
              {feeEarnerDescription}
            </MUITypography>
          </MUIStack>
        )
      }
    },
    {
      field: 'anticipatedDueDate',
      headerName: 'Anticipated due date',
      width: 180,
      type: 'string',
      hideable: false,
      renderCell: (params: any) => (
        <MUITypography sx={{ mb: 0 }}>
          {params.row.anticipatedDueDate ? formatDate(params.row.anticipatedDueDate) : '-'}
        </MUITypography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      minWidth: 160,
      maxWidth: 180,
      flex: 1,
      type: 'string',
      hideable: false,
      renderCell: (params: any) => {
        const status = params.value?.toLowerCase() || ''
        let backgroundColor = 'primary.main'
        let label = params.value || 'Not set'

        if (status.includes('discharged')) {
          backgroundColor = 'success.main'
        } else if (status.includes('outstanding') || status.includes('not possible')) {
          backgroundColor = 'warning.light'
        } else if (status.includes('select')) {
          label = 'Not set'
        }

        return <Pill label={label} backgroundColor={backgroundColor} color={'white'} />
      }
    },
    {
      field: 'moreDetails',
      headerName: 'More details',
      width: 140,
      type: 'custom',
      hideable: false,
      renderCell: (params: any) => (
        <MUITypography
          variant="body2"
          sx={{
            mb: 0,
            fontSize: '0.875rem',
            color: theme.palette.primary.main,
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
          onClick={() => handleNavigate({ clientNo: params.row.clientNo, matterNo: params.row.matterNo })}
        >
          View details
        </MUITypography>
      )
    }
  ]

  return (
    <MUIPaper
      data-testid="UndertakingsTableModule"
      sx={{
        boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0)',
        fontWeight: 400,
        height: 'auto',
        mt: pageTitleBlockIsVisible ? 0 : '12rem',
        width: '100%',
        [`& .perfect-portal-data-grid-header`]: {
          fontWeight: 900,
          [`&.case-type-header`]: {
            borderRight: '1px solid #e0e0e0',
            [`& .MuiDataGrid-columnSeparator`]: {
              display: 'none'
            }
          }
        },
        [`& .MuiDataGrid-cell`]: {
          display: 'flex',
          alignItems: 'center'
        },
        [`& .MuiDataGrid-footerContainer`]: {
          display: 'none'
        }
      }}
    >
      <DataGridPro
        columns={columns}
        loading={loading}
        disableColumnSorting
        disableColumnFilter
        disableColumnSelector
        disableColumnMenu={true}
        disableVirtualization
        getRowId={(row) => `${row.clientMatterNo}-${uuidv4()}`}
        rows={undertakings ?? []}
        pinnedColumns={{ left: ['spacer', 'clientMatterNo'], right: ['status', 'moreDetails'] }}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton'
          }
        }}
        slots={{
          headerFilterMenu: null
        }}
        sx={{
          borderTop: 'none',
          [`.MuiDataGrid-root`]: { border: 'none', height: 'auto' },
          [`& .Mui-selected`]: { backgroundColor: 'transparent !important' },
          [`& [data-field="clientMatterNo"]`]: { justifyContent: 'center' },
          [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
          [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: { outline: 'none' },
          [`* .${gridClasses.columnHeader}`]: { fontSize: '0.875rem' }
        }}
      />
    </MUIPaper>
  )
}
